import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgModel,
  ReactiveFormsModule
} from '@angular/forms';
import { GroupedPassedItem, Item } from '@approot/models';
import { Utilities } from '@approot/utilities';
import { NgSelectModule } from '@ng-select/ng-select';

// For these fields, don't allow "Custom" to be directly selected,
// only is schedule changed or set from backend.
const FrequencyFields = [
  'frequency',
  'exerciseFrequency',
  'floatingLegFrequency',
  'fixedLegFrequency'
];

@Component({
  standalone: true,
  selector: 'app-selector',
  templateUrl: './selector.component.html',
  styleUrls: ['./selector.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, CommonModule, NgSelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectorComponent),
      multi: true
    }
  ], // implements the ControlValueAcessor, used to set up sync with FormControl
  encapsulation: ViewEncapsulation.None
})
export class SelectorComponent
  implements OnInit, ControlValueAccessor, OnChanges, AfterViewInit
{
  @ViewChild('selectModel', { static: false }) public selectModel: NgModel;
  @Input() public multiSelect = false;
  @Input() public closeOnSelect = true;
  @Input() public isSearch: boolean; // does the component require the ability to search for the value?
  @Input() public passedItems: GroupedPassedItem[] | string[] | number[]; // instead of passing in a attribute, if the data is static, we can pass in an array of items
  @Input() public id: string; // the id of the component
  @Input() public defaultValue: string; // default value that should be set as the selector
  @Input() public isCross: boolean; // Is this from the 'cross' attribute of the leg component?
  @Input() public textColor: string;
  @Input() public info = '';
  @Input() public readonly: boolean;
  @Input() public placeholder = '';
  @Output() public valueEmitter = new EventEmitter<{
    value: any;
    userSelect: boolean;
  }>();
  @Output() public indexSelected = new EventEmitter<number | number[]>();
  @Output() public onFocus = new EventEmitter<void>();
  @Output() public onBlur = new EventEmitter<void>();
  public items: Item[] = []; // items of dropdown
  public value: string | string[];
  public disabled: boolean;
  public formControl: AbstractControl;
  private onTouched: Function;
  private shouldEmit = true;
  private onChange: Function; // callback function to register changes
  constructor(private injector: Injector) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.passedItems) {
      this.formatPassedItems();
      this.maybeRemoveCustomFromPassedItems();
    }
  }

  public ngOnInit(): void {
    if (this.passedItems) {
      this.maybeRemoveCustomFromPassedItems();
    }

    if (this.defaultValue) {
      // if there is a default value, set it
      this.value = this.defaultValue;
      this.emitValueChanges();
    }
  }

  public ngAfterViewInit(): void {
    const ngControl = this.injector.get<NgControl>(NgControl, null);

    if (ngControl?.control) {
      // same as ngControl && ngControl.control
      // if there is a form control passed in set the validators, so the selector goes red
      this.formControl = ngControl.control;
      this.selectModel.control.setValidators(this.formControl.validator);
      this.formControl.valueChanges.subscribe(val => {
        if (this.shouldEmit) {
          // we don't want to emit a value if we are doing a manual change
          this.valueEmitter.emit({ value: val, userSelect: false });
        }
        // when the value changes we need to emit changes from
        // the component
        this.shouldEmit = true;
      });
    }
  }

  public onChangeLocal(optionSelected: Item | Item[]): void {
    this.shouldEmit = false; // don't emit a valueEmitter from this.formControl.valueChanges observable
    if (optionSelected) {
      if (Array.isArray(optionSelected)) {
        this.value = optionSelected.map(item => item.name);
        this.indexSelected.emit(optionSelected.map(item => item.id));
      } else {
        this.value = optionSelected.name;
        this.indexSelected.emit(optionSelected.id);
      }
    } else {
      this.value = null;
    }

    this.emitValueChanges();
  }

  public onElemBlur(): void {
    if (this.formControl?.updateOn === 'blur') {
      this.onTouched();
    }
    this.onBlur.emit();
  }

  public onElemFocus(): void {
    this.onFocus.emit();
  }

  public writeValue(value: string): void {
    const currentItems = this.items.map(item => item.name);
    if (value === Utilities.customText && !currentItems.includes(value)) {
      this.items.push({
        name: Utilities.customText,
        id: this.items.length
      });
    }

    this.value = value;
  }

  public registerOnChange(fn: Function): void {
    this.onChange = fn; // we save a reference to the callback function  that is passed by the from control form
    // we will trigger it every time there is a change in the selected value
  }

  public registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private emitValueChanges(): void {
    if (this.formControl && this.onChange) {
      this.onChange(this.value);
    }

    this.valueEmitter.emit({ value: this.value, userSelect: true });
  }

  private maybeRemoveCustomFromPassedItems(): void {
    if (FrequencyFields.includes(this.id) && !this.readonly) {
      this.items = this.items.filter(v => v.name !== Utilities.customText);
    }
  }

  private formatPassedItems(): void {
    const passedGroupedItems =
      this.passedItems.length && typeof this.passedItems[0] !== 'object'
        ? [{ items: this.passedItems }] // for backwards compatibility
        : this.passedItems;

    this.items = passedGroupedItems
      .map(passedItemsGroup => {
        return passedItemsGroup.items.map(y => {
          return { group: passedItemsGroup.group, name: y };
        });
      })
      .flatMap(item => item)
      .map((element, index) => {
        return { group: element.group, name: element.name, id: index };
      });
  }
}
